import React, { useState } from 'react';
import {
  MessageSquare, Users, Clock, Pin, Star, ThumbsUp, 
  Reply, Search, Filter, Plus, Tag,
  TrendingUp, Eye, MessageCircle, Award, Shield,
  ChevronRight, User, Calendar, Flame, Target,
  BookOpen, Code, Lightbulb, HelpCircle, Sparkles,
  MoreVertical, Flag, Share2, Bookmark, Edit, CheckCircle
} from 'lucide-react';

interface ForumPost {
  id: string;
  title: string;
  content: string;
  author: {
    name: string;
    avatar: string;
    role: 'student' | 'instructor' | 'moderator' | 'admin';
    reputation: number;
    joinDate: string;
  };
  category: string;
  tags: string[];
  replies: number;
  views: number;
  likes: number;
  dislikes: number;
  createdAt: string;
  lastActivity: string;
  isPinned: boolean;
  isSolved: boolean;
  isLocked: boolean;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
}

interface ForumCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  postCount: number;
  lastPost?: {
    title: string;
    author: string;
    date: string;
  };
}

export default function Forums() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'trending' | 'unanswered'>('latest');
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [showCreatePost, setShowCreatePost] = useState(false);

  const categories: ForumCategory[] = [
    {
      id: 'general',
      name: 'General Discussion',
      description: 'General topics and community discussions',
      icon: <MessageSquare className="w-5 h-5" />,
      color: 'from-blue-500 to-indigo-600',
      postCount: 1243,
      lastPost: {
        title: 'Welcome to the new learning platform!',
        author: 'Sarah Chen',
        date: '2 hours ago'
      }
    },
    {
      id: 'help',
      name: 'Help & Support',
      description: 'Get help with courses and technical issues',
      icon: <HelpCircle className="w-5 h-5" />,
      color: 'from-green-500 to-emerald-600',
      postCount: 892,
      lastPost: {
        title: 'Issues with video playback',
        author: 'John Doe',
        date: '30 minutes ago'
      }
    },
    {
      id: 'programming',
      name: 'Programming & Development',
      description: 'Coding questions, best practices, and project discussions',
      icon: <Code className="w-5 h-5" />,
      color: 'from-purple-500 to-pink-600',
      postCount: 2156,
      lastPost: {
        title: 'Best practices for React hooks',
        author: 'Alex Kumar',
        date: '1 hour ago'
      }
    },
    {
      id: 'career',
      name: 'Career Guidance',
      description: 'Career advice, job opportunities, and professional development',
      icon: <Target className="w-5 h-5" />,
      color: 'from-orange-500 to-red-600',
      postCount: 567,
      lastPost: {
        title: 'Transitioning to data science',
        author: 'Maria Garcia',
        date: '3 hours ago'
      }
    },
    {
      id: 'projects',
      name: 'Project Showcase',
      description: 'Share your projects and get feedback from the community',
      icon: <Sparkles className="w-5 h-5" />,
      color: 'from-cyan-500 to-blue-600',
      postCount: 334,
      lastPost: {
        title: 'My first React portfolio website',
        author: 'Emma Wilson',
        date: '4 hours ago'
      }
    },
    {
      id: 'study-tips',
      name: 'Study Tips & Resources',
      description: 'Learning strategies, study methods, and helpful resources',
      icon: <Lightbulb className="w-5 h-5" />,
      color: 'from-yellow-500 to-orange-600',
      postCount: 445,
      lastPost: {
        title: 'Effective note-taking techniques',
        author: 'David Lee',
        date: '5 hours ago'
      }
    }
  ];

  const forumPosts: ForumPost[] = [
    {
      id: '1',
      title: 'How to optimize React component performance?',
      content: 'I\'m working on a large React application and noticing performance issues. What are the best practices for optimizing component performance?',
      author: {
        name: 'John Developer',
        avatar: '👨‍💻',
        role: 'student',
        reputation: 245,
        joinDate: '2023-08-15'
      },
      category: 'programming',
      tags: ['react', 'performance', 'optimization', 'javascript'],
      replies: 15,
      views: 342,
      likes: 23,
      dislikes: 2,
      createdAt: '2024-01-30T10:30:00',
      lastActivity: '2024-01-30T14:45:00',
      isPinned: false,
      isSolved: true,
      isLocked: false,
      difficulty: 'intermediate'
    },
    {
      id: '2',
      title: 'Welcome to our new learning community!',
      content: 'We\'re excited to launch this new forum where learners can connect, share knowledge, and support each other. Please introduce yourself!',
      author: {
        name: 'Sarah Chen',
        avatar: '👩‍🏫',
        role: 'instructor',
        reputation: 1250,
        joinDate: '2023-01-01'
      },
      category: 'general',
      tags: ['welcome', 'community', 'introduction'],
      replies: 89,
      views: 1567,
      likes: 156,
      dislikes: 3,
      createdAt: '2024-01-28T09:00:00',
      lastActivity: '2024-01-30T16:20:00',
      isPinned: true,
      isSolved: false,
      isLocked: false,
      difficulty: 'beginner'
    },
    {
      id: '3',
      title: 'Career transition from marketing to data science',
      content: 'I have 5 years of marketing experience and want to transition to data science. What learning path would you recommend?',
      author: {
        name: 'Maria Garcia',
        avatar: '👩‍💼',
        role: 'student',
        reputation: 89,
        joinDate: '2024-01-15'
      },
      category: 'career',
      tags: ['career-change', 'data-science', 'learning-path', 'advice'],
      replies: 12,
      views: 234,
      likes: 18,
      dislikes: 0,
      createdAt: '2024-01-30T08:15:00',
      lastActivity: '2024-01-30T15:30:00',
      isPinned: false,
      isSolved: false,
      isLocked: false,
      difficulty: 'beginner'
    },
    {
      id: '4',
      title: 'Issue with video playback in Chrome browser',
      content: 'Videos won\'t load properly in Chrome. They work fine in Firefox. Anyone else experiencing this?',
      author: {
        name: 'Alex Kumar',
        avatar: '👨‍🔧',
        role: 'student',
        reputation: 156,
        joinDate: '2023-11-20'
      },
      category: 'help',
      tags: ['technical-issue', 'browser', 'video', 'chrome'],
      replies: 7,
      views: 123,
      likes: 5,
      dislikes: 1,
      createdAt: '2024-01-30T11:45:00',
      lastActivity: '2024-01-30T13:20:00',
      isPinned: false,
      isSolved: false,
      isLocked: false,
      difficulty: 'beginner'
    },
    {
      id: '5',
      title: 'My first full-stack project - feedback welcome!',
      content: 'I just completed my first full-stack web application using React and Node.js. Would love to get some feedback from the community!',
      author: {
        name: 'Emma Wilson',
        avatar: '👩‍💻',
        role: 'student',
        reputation: 67,
        joinDate: '2023-12-01'
      },
      category: 'projects',
      tags: ['project', 'full-stack', 'react', 'nodejs', 'feedback'],
      replies: 9,
      views: 189,
      likes: 14,
      dislikes: 0,
      createdAt: '2024-01-29T16:30:00',
      lastActivity: '2024-01-30T12:10:00',
      isPinned: false,
      isSolved: false,
      isLocked: false,
      difficulty: 'intermediate'
    }
  ];

  const popularTags = [
    'react', 'javascript', 'python', 'data-science', 'career-advice',
    'machine-learning', 'web-development', 'algorithms', 'projects',
    'best-practices', 'debugging', 'performance'
  ];

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'instructor': return <Award className="w-4 h-4 text-yellow-500" />;
      case 'moderator': return <Shield className="w-4 h-4 text-blue-500" />;
      case 'admin': return <Shield className="w-4 h-4 text-red-500" />;
      default: return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-600 bg-green-50';
      case 'intermediate': return 'text-yellow-600 bg-yellow-50';
      case 'advanced': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString();
  };

  const filteredPosts = forumPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          post.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          post.author.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesTags = filterTags.length === 0 || filterTags.some(tag => post.tags.includes(tag));
    return matchesSearch && matchesCategory && matchesTags;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <MessageSquare className="w-7 h-7" />
              Community Forums
            </h1>
            <p className="text-indigo-100">
              Connect with fellow learners, ask questions, and share knowledge
            </p>
          </div>
          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors">
              <TrendingUp className="w-5 h-5" />
              <span className="hidden sm:inline">Trending</span>
            </button>
            <button
              onClick={() => setShowCreatePost(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-medium"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">New Post</span>
            </button>
          </div>
        </div>

        {/* Forum Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <MessageSquare className="w-6 h-6 mx-auto mb-2 text-indigo-200" />
            <p className="text-2xl font-bold">5,637</p>
            <p className="text-xs text-indigo-100">Total Posts</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Users className="w-6 h-6 mx-auto mb-2 text-indigo-200" />
            <p className="text-2xl font-bold">1,234</p>
            <p className="text-xs text-indigo-100">Active Members</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Eye className="w-6 h-6 mx-auto mb-2 text-indigo-200" />
            <p className="text-2xl font-bold">45.2K</p>
            <p className="text-xs text-indigo-100">Total Views</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Flame className="w-6 h-6 mx-auto mb-2 text-indigo-200" />
            <p className="text-2xl font-bold">89</p>
            <p className="text-xs text-indigo-100">Hot Topics</p>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Forum Categories</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  selectedCategory === category.id
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={`p-2 rounded-lg bg-gradient-to-r ${category.color} text-white`}>
                    {category.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{category.name}</h3>
                    <p className="text-xs text-gray-600">{category.postCount} posts</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-2">{category.description}</p>
                {category.lastPost && (
                  <div className="text-xs text-gray-500">
                    <p className="truncate">Latest: {category.lastPost.title}</p>
                    <p>by {category.lastPost.author} • {category.lastPost.date}</p>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search posts, topics, or users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="latest">Latest Posts</option>
            <option value="popular">Most Popular</option>
            <option value="trending">Trending</option>
            <option value="unanswered">Unanswered</option>
          </select>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50">
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>

        {/* Popular Tags */}
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Popular Tags</h4>
          <div className="flex flex-wrap gap-2">
            {popularTags.slice(0, 8).map((tag) => (
              <button
                key={tag}
                onClick={() => setFilterTags(prev => 
                  prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
                )}
                className={`px-2 py-1 text-xs rounded-full transition-colors ${
                  filterTags.includes(tag)
                    ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                #{tag}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Forum Posts */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6">
          <div className="space-y-4">
            {filteredPosts.map((post) => (
              <div
                key={post.id}
                className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="text-3xl">{post.author.avatar}</div>
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {post.isPinned && <Pin className="w-4 h-4 text-indigo-600" />}
                          {post.isSolved && <CheckCircle className="w-4 h-4 text-green-600" />}
                          <h3 className="text-lg font-semibold text-gray-900 hover:text-indigo-600 cursor-pointer">
                            {post.title}
                          </h3>
                        </div>
                        
                        <p className="text-gray-600 text-sm line-clamp-2 mb-3">{post.content}</p>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                          <div className="flex items-center gap-1">
                            {getRoleIcon(post.author.role)}
                            <span className="font-medium">{post.author.name}</span>
                            <span className="text-gray-400">•</span>
                            <span>{post.author.reputation} rep</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(post.createdAt)}</span>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getDifficultyColor(post.difficulty)}`}>
                            {post.difficulty}
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                          {post.tags.map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <MessageCircle className="w-4 h-4" />
                          <span>{post.replies}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          <span>{post.views}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <ThumbsUp className="w-4 h-4" />
                          <span>{post.likes}</span>
                        </div>
                        <span className="text-gray-400">Last activity: {formatDate(post.lastActivity)}</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                          <Bookmark className="w-4 h-4" />
                        </button>
                        <button className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                          <Share2 className="w-4 h-4" />
                        </button>
                        <button className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">
                          <Reply className="w-4 h-4" />
                          Reply
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No posts found</h3>
              <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Post Modal */}
      {showCreatePost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Create New Post</h3>
                <button
                  onClick={() => setShowCreatePost(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    placeholder="Enter your question or topic title..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="">Select a category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                  <input
                    type="text"
                    placeholder="Add tags separated by commas (e.g. react, javascript, help)"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                  <textarea
                    placeholder="Describe your question or share your thoughts..."
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreatePost(false)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Create Post
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}